import {
	Box,
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	type PaperProps,
	Stack,
	SvgIcon,
	Typography,
} from '@mui/material';
import { Link as RouterLink } from 'found';
import { motion, useReducedMotion } from 'framer-motion';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { borderRadius, breakpoints, palette, spaces } from '../../assets/styles/themes/tokens';
import { ampliTrackTrialReportCardMessageDisplayedEvent } from '../../services/track.service';
import getMuiIcon from '../../utils/getMuiIcon';
import { COMPLETE_AREA_GRADE, formatGradeValue, isPerfectGrade } from './grade';
import { reportCardAreas } from './reportCard.config';
import type { TrialReportCardProgress } from './useTrialReportCardProgress';

interface TrialReportCardDialogProps {
	open: boolean;
	onClose: () => void;
	firstName?: string;
	progress: TrialReportCardProgress;
	/** Viewport point the opening animation grows from (the click on the sidebar card). */
	openOrigin?: { x: number; y: number };
}

// A completed area always scores the best grade (1,0).
const COMPLETED_AREA_LABEL = formatGradeValue(COMPLETE_AREA_GRADE);

const AnimatedPaper = motion(Paper);

// The HTML animation/drag handler props collide with framer-motion's
// identically named motion props, so they're excluded from the pass-through.
type DialogPaperProps = Omit<
	PaperProps,
	'onAnimationStart' | 'onDrag' | 'onDragStart' | 'onDragEnd'
>;

// MUI's Fade still owns opacity (backdrop + paper); this adds an ease-out
// scale each time the dialog opens, growing from the click point when one is
// given. The factory binds the origin so the component identity only changes
// per open (a stable identity keeps re-renders from replaying the entrance).
const createDialogPaper = (openOrigin?: { x: number; y: number }) => {
	const DialogPaper = (props: DialogPaperProps) => {
		const reducedMotion = useReducedMotion();
		const paperRef = useRef<HTMLDivElement>(null);
		const [transformOrigin, setTransformOrigin] = useState<string>();

		// Measured before first paint so the entrance never flashes centre-origin.
		// biome-ignore lint/correctness/useExhaustiveDependencies: openOrigin is bound per-open by the factory closure; the measurement must run exactly once on mount
		useLayoutEffect(() => {
			if (!openOrigin || !paperRef.current) return;
			const rect = paperRef.current.getBoundingClientRect();
			setTransformOrigin(
				`${Math.round(openOrigin.x - rect.left)}px ${Math.round(openOrigin.y - rect.top)}px`
			);
		}, []);

		const { sx, ...paperProps } = props;

		return (
			<AnimatedPaper
				{...paperProps}
				ref={paperRef}
				sx={[...(Array.isArray(sx) ? sx : [sx]), !!transformOrigin && { transformOrigin }]}
				initial={reducedMotion ? false : { scale: 0.92 }}
				animate={{ scale: 1 }}
				transition={{ duration: 0.25, ease: 'easeOut' }}
			/>
		);
	};
	return DialogPaper;
};

const paperStyles = {
	borderRadius: borderRadius.md,
	width: '100%',
	maxWidth: '420px',
	// On phones, use 16px side gutters instead of MUI's default 32px paper margin
	// so the dialog uses more of the narrow screen.
	[`@media (max-width:${breakpoints.mobile}px)`]: {
		marginLeft: spaces.md,
		marginRight: spaces.md,
	},
};

const titleStyles = {
	position: 'relative',
	padding: spaces.lg,
	paddingBottom: spaces.xs,
	textAlign: 'center',
};

const closeButtonStyles = {
	position: 'absolute',
	top: spaces.xs,
	right: spaces.xs,
	padding: spaces.xxs,
	color: palette.text.secondary,
	'&:hover': { color: palette.text.primary },
};

const headingWrapperStyles = {
	position: 'relative',
	display: 'inline-block',
	paddingBottom: '4px',
};

const headingUnderlineStyles = {
	position: 'absolute',
	left: 0,
	right: 0,
	bottom: 0,
	height: '3px',
	borderRadius: '3px',
	backgroundColor: palette.accent.pink,
	transform: 'rotate(-0.8deg)',
};

const rowStyles = {
	justifyContent: 'space-between',
	alignItems: 'center',
	gap: spaces.sm,
	paddingY: spaces.sm,
	borderTop: `1px solid ${palette.elevation.outlined}`,
};

const areaLabelStyles = {
	fontWeight: 600,
	color: palette.text.primary,
};

const stateStyles = {
	fontWeight: 700,
	// A completed area always scores 1,0, which the design colours green.
	color: palette.success.main,
	fontVariantNumeric: 'tabular-nums',
	whiteSpace: 'nowrap',
};

const ungradedStateStyles = {
	fontStyle: 'italic',
	color: palette.text.secondary,
	whiteSpace: 'nowrap',
};

const totalRowStyles = {
	justifyContent: 'space-between',
	alignItems: 'baseline',
	marginTop: spaces.xxs,
	paddingTop: spaces.sm,
	borderTop: `2px solid ${palette.text.primary}`,
};

const totalLabelStyles = {
	fontWeight: 600,
	textTransform: 'uppercase',
	letterSpacing: '0.1em',
	color: palette.text.secondary,
};

const totalValueStyles = {
	fontVariantNumeric: 'tabular-nums',
};

const TrialReportCardDialog: React.FC<TrialReportCardDialogProps> = ({
	open,
	onClose,
	firstName,
	progress,
	openOrigin,
}) => {
	const DialogPaper = useMemo(() => createDialogPaper(openOrigin), [openOrigin]);
	const { t } = useTranslation();
	const { completionByArea, grade } = progress;
	const ungradedLabel = t('Not graded');
	// Overall grade is blue (the in-app accent) until every area is done, then green.
	const totalValueColor = isPerfectGrade(grade) ? palette.success.main : palette.primary.main;

	useEffect(() => {
		if (open) {
			ampliTrackTrialReportCardMessageDisplayedEvent();
		}
		// Fire once per open; progress changes while the dialog is open must not
		// re-fire the event.
	}, [open]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			data-testid="trial-report-card-dialog"
			PaperComponent={DialogPaper}
			PaperProps={{ sx: paperStyles }}
		>
			<DialogTitle component="div" sx={titleStyles}>
				<IconButton
					onClick={onClose}
					aria-label={t('Close')}
					data-testid="trial-report-card-close"
					sx={closeButtonStyles}
				>
					<SvgIcon component={getMuiIcon('close')} sx={{ color: 'inherit' }} aria-hidden="true" />
				</IconButton>
				<Box sx={headingWrapperStyles}>
					<Typography
						variant="h5"
						data-testid="trial-report-card-dialog-title"
						sx={{ color: palette.text.primary }}
					>
						{t('Your Report Card')}
					</Typography>
					<Box sx={headingUnderlineStyles} aria-hidden="true" />
				</Box>
				<Typography
					variant="body2"
					data-testid="trial-report-card-dialog-subtitle"
					sx={{ marginTop: spaces.sm, color: palette.text.secondary }}
				>
					{t('School Year 2026 / 2027 · {{firstName}}', { firstName })}
				</Typography>
				<Typography
					variant="body2"
					data-testid="trial-report-card-encouragement"
					sx={{ marginTop: spaces.xs, color: palette.text.secondary }}
				>
					{!isPerfectGrade(grade) && t('Try our key features and work your way toward a perfect grade.')}
				</Typography>
			</DialogTitle>
			<DialogContent sx={{ padding: spaces.lg, paddingTop: 0 }}>
				{reportCardAreas.map((area) => {
					const isComplete = completionByArea[area.id];
					return (
						<Stack
							key={area.id}
							direction="row"
							sx={rowStyles}
							data-testid={`trial-report-card-row-${area.id}`}
						>
							<Typography
								variant="subtitle1"
								data-testid={`trial-report-card-row-${area.id}-label`}
								sx={areaLabelStyles}
							>
								{t(area.labelKey)}
							</Typography>
							{isComplete ? (
								<Typography
									variant="subtitle1"
									data-testid={`trial-report-card-row-${area.id}-grade`}
									sx={stateStyles}
								>
									{COMPLETED_AREA_LABEL}
								</Typography>
							) : (
								<Stack direction="row" spacing={spaces.sm} alignItems="center">
									<Typography
										variant="body2"
										data-testid={`trial-report-card-row-${area.id}-ungraded`}
										sx={ungradedStateStyles}
									>
										{ungradedLabel}
									</Typography>
									<Button
										variant="contained"
										size="small"
										color="primary"
										component={RouterLink}
										to={area.ctaRoute}
										onClick={onClose}
										data-testid={`trial-report-card-row-${area.id}-cta`}
									>
										{t('Get started')}
									</Button>
								</Stack>
							)}
						</Stack>
					);
				})}
				<Stack direction="row" sx={totalRowStyles}>
					<Typography
						variant="overline"
						data-testid="trial-report-card-total-label"
						sx={totalLabelStyles}
					>
						{t('Overall Grade')}
					</Typography>
					{grade.label ? (
						<Typography
							variant="h4"
							data-testid="trial-report-card-total-grade"
							sx={{ ...totalValueStyles, color: totalValueColor }}
						>
							{grade.label}
						</Typography>
					) : (
						<Typography
							variant="body2"
							data-testid="trial-report-card-total-ungraded"
							sx={ungradedStateStyles}
						>
							{ungradedLabel}
						</Typography>
					)}
				</Stack>
				{isPerfectGrade(grade) && (
					<Typography
						variant="body2"
						data-testid="trial-report-card-praise"
						sx={{ marginTop: spaces.sm, color: palette.text.secondary }}
					>
						{t(
							'Top marks. You now know how to find and download the right teaching materials, create your own content with Muki, and keep everything organized in collections. Overall assessment: passed with flying colors. Keep it up.'
						)}
					</Typography>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default TrialReportCardDialog;
